import React, { useState, useContext, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  InputAdornment,
  IconButton
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useTranslation } from 'react-i18next'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { LoadingContext } from 'contexts/LoadingContext'
import { validatePasswordComplexity } from 'utils/formatUtils'
import { requestPasswordReset, confirmPasswordReset } from 'api/auth'
import FixedWidthContainer from 'components/FixedWidthContainer'
import SectionHeader from 'components/SectionHeader'
import DealerSignupSection from 'components/DealerSignupSection'

interface ResetPasswordFormValues {
  email: string
  code: string
  newPassword: string
  confirmNewPassword: string
}

const ResetPasswordPage = () => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<ResetPasswordFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur'
  })

  const handleTrimmedChange = (
    field: keyof ResetPasswordFormValues,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const trimmedValue = e.target.value.replace(/\s/g, '')
    setValue(field, trimmedValue)
  }

  const { showMessage } = useContext(SnackbarContext)
  const { showLoading, stopLoading } = useContext(LoadingContext)
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [resendLabel, setResendLabel] = useState('Send Code')
  const [timer, setTimer] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    let countdown: NodeJS.Timeout
    if (timer > 0) {
      countdown = setTimeout(() => {
        setTimer(prev => prev - 1)
      }, 1000)
    } else if (timer === 0 && isResendDisabled) {
      setIsResendDisabled(false)
      setResendLabel('Resend Code')
    }
    return () => clearTimeout(countdown)
  }, [timer, isResendDisabled])

  const handleSendVerificationCode = async () => {
    const email = getValues('email')
    if (!email) {
      showMessage(t('snackbar.passwordReset.emailRequired'), 'error')
      return
    }
    setIsResendDisabled(true)
    setResendLabel('Resend Code')
    setTimer(30)

    const result = await requestPasswordReset(email)
    if (result.success) {
      showMessage(t('snackbar.passwordReset.codeSent'))
      setStep(2)
    } else {
      showMessage(result.error, 'error')
      setIsResendDisabled(false)
      setTimer(0)
    }
  }

  const handleNewPasswordBlur = () => {
    const newPasswordValue = watch('newPassword')
    const errorMessage = validatePasswordComplexity(newPasswordValue)
    if (errorMessage) {
      setError('newPassword', { type: 'manual', message: errorMessage })
    } else {
      clearErrors('newPassword')
    }
  }

  const handleConfirmPasswordBlur = () => {
    const newPasswordValue = watch('newPassword')
    const confirmValue = watch('confirmNewPassword')
    if (newPasswordValue !== confirmValue) {
      setError('confirmNewPassword', {
        type: 'manual',
        message: 'Passwords do not match.'
      })
    } else {
      clearErrors('confirmNewPassword')
    }
  }

  const onSubmit: SubmitHandler<ResetPasswordFormValues> = async data => {
    if (step === 1) {
      await handleSendVerificationCode()
    } else if (step === 2) {
      showLoading()
      const result = await confirmPasswordReset(
        data.code,
        data.newPassword,
        data.email
      )
      stopLoading()
      if (result.success) {
        showMessage(t('snackbar.passwordReset.resetSuccess'))
        navigate('/login')
      } else {
        showMessage(
          'Password reset failed. Please check your code and try again.',
          'error'
        )
      }
    }
  }

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <FixedWidthContainer sx={{ pb: 8 }}>
        <SectionHeader title='RESET PASSWORD' />
        <Box sx={{ maxWidth: '648px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {step === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label='Email Address'
                    variant='outlined'
                    fullWidth
                    defaultValue=''
                    {...register('email', {
                      required: 'Email address is required.'
                    })}
                    onChange={e =>
                      handleTrimmedChange(
                        'email',
                        e as React.ChangeEvent<HTMLInputElement>
                      )
                    }
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-input': { color: 'black' },
                      '& .MuiInputLabel-root': { color: '#7C7C7C' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    variant='contained'
                    color='primary'
                    fullWidth
                    type='submit'
                  >
                    Next
                  </Button>
                </Grid>
              </Grid>
            )}
            {step === 2 && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label='Verification Code'
                      variant='outlined'
                      fullWidth
                      autoComplete='one-time-code'
                      defaultValue=''
                      {...register('code', {
                        required: 'Verification code is required.'
                      })}
                      onChange={e =>
                        handleTrimmedChange(
                          'code',
                          e as React.ChangeEvent<HTMLInputElement>
                        )
                      }
                      error={!!errors.code}
                      helperText={errors.code?.message}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        'data-form-type': 'other',
                        'data-lpignore': 'true',
                        autoComplete: 'one-time-code'
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <Button
                              onClick={handleSendVerificationCode}
                              disabled={isResendDisabled}
                            >
                              {resendLabel}
                            </Button>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiInputBase-input': { color: 'black' },
                        '& .MuiInputLabel-root': { color: '#7C7C7C' }
                      }}
                    />
                    {isResendDisabled && (
                      <Typography variant='body2' sx={{ mt: 1 }}>
                        {timer} seconds left before you can resend.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='New Password'
                      variant='outlined'
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      defaultValue=''
                      {...register('newPassword', {
                        required: 'New password is required.',
                        validate: value =>
                          validatePasswordComplexity(value) || true
                      })}
                      onChange={e =>
                        handleTrimmedChange(
                          'newPassword',
                          e as React.ChangeEvent<HTMLInputElement>
                        )
                      }
                      error={!!errors.newPassword}
                      helperText={errors.newPassword?.message}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 64 }}
                      onBlur={handleNewPasswordBlur}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={() => setShowPassword(prev => !prev)}
                              edge='end'
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiInputBase-input': { color: 'black' },
                        '& .MuiInputLabel-root': { color: '#7C7C7C' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label='Confirm New Password'
                      variant='outlined'
                      fullWidth
                      type={showConfirmPassword ? 'text' : 'password'}
                      defaultValue=''
                      {...register('confirmNewPassword', {
                        required: 'Please confirm your new password.',
                        validate: value =>
                          value === watch('newPassword') ||
                          'Passwords do not match.'
                      })}
                      onChange={e =>
                        handleTrimmedChange(
                          'confirmNewPassword',
                          e as React.ChangeEvent<HTMLInputElement>
                        )
                      }
                      error={!!errors.confirmNewPassword}
                      helperText={errors.confirmNewPassword?.message}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 64 }}
                      onBlur={handleConfirmPasswordBlur}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(prev => !prev)
                              }
                              edge='end'
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiInputBase-input': { color: 'black' },
                        '& .MuiInputLabel-root': { color: '#7C7C7C' }
                      }}
                    />
                  </Grid>
                </Grid>
                <Box
                  display='flex'
                  justifyContent='space-between'
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant='outlined'
                    color='secondary'
                    onClick={() => setStep(1)}
                  >
                    Back
                  </Button>
                  <Button variant='contained' color='primary' type='submit'>
                    Reset Password
                  </Button>
                </Box>
              </>
            )}
          </form>
        </Box>
      </FixedWidthContainer>
      <Box sx={{ mt: 'auto' }}>
        <DealerSignupSection />
      </Box>
    </Box>
  )
}

export default ResetPasswordPage
